import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useActiveProperty } from "@/context/ActivePropertyContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AccountCombobox } from "@/components/AccountCombobox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, Trash2, Lock, LockOpen, CheckCircle2, Download,
  Loader2, ListChecks, PieChart, Settings2, Plus, AlertTriangle, Scale, Scissors, Wand2, X, ArrowUpDown, ChevronLeft, ChevronRight,
} from "lucide-react";

const money = (n) => {
  const v = Number(n || 0);
  return (v < 0 ? "-$" : "$") + Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const optLabel = (c) => (c.code ? `${c.code} · ${c.name}` : c.name);
const MASTER_COA_COUNT = 209;const thisMonth = () => new Date().toISOString().slice(0, 7);
const shiftMonth = (m, delta) => {
  const [y, mo] = (m || thisMonth()).split("-").map(Number);
  const d = new Date(y, mo - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function Accounting() {
  const { activePropertyId: propertyId, setActivePropertyId: setPropertyId } = useActiveProperty();
  const { user } = useAuth();
  const canReopen = user && (user.role === "accountant" || user.role === "admin");
  const [properties, setProperties] = useState([]);
  const [month, setMonth] = useState(thisMonth());
  const [tab, setTab] = useState("transactions");
  const [categories, setCategories] = useState([]);
  const [txnData, setTxnData] = useState({ transactions: [], uploads: [], summary: {} });
  const [pnl, setPnl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [onlyUncat, setOnlyUncat] = useState(false);
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  // upload / mapping
  const [accountLabel, setAccountLabel] = useState("");
  const [parseResult, setParseResult] = useState(null);
  const [mapping, setMapping] = useState({ date_col: 0, desc_col: 1, amount_mode: "single", amount_col: 2, debit_col: null, credit_col: null, has_header: true, expenses_positive: false });
  const [importing, setImporting] = useState(false);

  // finalize
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [signName, setSignName] = useState("");
  const [signTitle, setSignTitle] = useState("");
  const [finalizing, setFinalizing] = useState(false);

  // categories editor
  const [catOpen, setCatOpen] = useState(false);
  const [catDraft, setCatDraft] = useState([]);

  // bulk select + auto-categorize rules
  const [selected, setSelected] = useState(new Set());
  const [bulkCat, setBulkCat] = useState("");
  const [autoBusy, setAutoBusy] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rulesDraft, setRulesDraft] = useState([]);
  const [ruleTxn, setRuleTxn] = useState(null);
  const [quickKw, setQuickKw] = useState("");
  const [quickCat, setQuickCat] = useState("");
  const [quickKind, setQuickKind] = useState("any");
  const [quickAction, setQuickAction] = useState("categorize");

  // split
  const [splitTxn, setSplitTxn] = useState(null);
  const [splitLines, setSplitLines] = useState([]);

  // balance sheet
  const [bs, setBs] = useState(null);
  const [bsSaving, setBsSaving] = useState(false);

  // P&L category drill-down
  const [drillCat, setDrillCat] = useState(null); // {id, name}

  useEffect(() => {
    api.get("/properties").then((r) => {
      setProperties(r.data);
      if (!propertyId && r.data[0]) setPropertyId(r.data[0].id);
    }).catch(() => {});
  }, []); // eslint-disable-line

  const loadCategories = useCallback(() => {
    if (!propertyId) return;
    api.get("/accounting/categories", { params: { property_id: propertyId } })
      .then((r) => setCategories(r.data.categories || [])).catch(() => {});
  }, [propertyId]);

  const loadData = useCallback(() => {
    if (!propertyId || !month) return;
    setLoading(true);
    Promise.all([
      api.get("/accounting/transactions", { params: { property_id: propertyId, month } }),
      api.get("/accounting/pnl", { params: { property_id: propertyId, month } }),
      api.get("/accounting/balance-sheet", { params: { property_id: propertyId, month } }),
    ]).then(([t, p, b]) => {
      setTxnData(t.data);
      setPnl(p.data);
      setBs(b.data);
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [propertyId, month]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadData(); }, [loadData]);

  const isFinal = pnl?.status === "final";
  const expenseCats = categories.filter((c) => c.type === "expense");
  const incomeCats = categories.filter((c) => c.type === "income");
  const nonPnlCats = categories.filter((c) => c.type === "non_pnl");
  const catById = Object.fromEntries(categories.map((c) => [c.id, c]));
  const catLabel = (id) => { const c = catById[id]; return c ? (c.code ? `${c.code} · ${c.name}` : c.name) : id; };
  const groups = [...new Set(categories.map((c) => c.group))];

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!propertyId || !month) { toast.error("Pick a property and month first"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/accounting/uploads/parse", fd, {
        params: { property_id: propertyId, month, account_label: accountLabel || file.name },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParseResult(r.data);
      if (r.data.mode === "pdf") return; // AI-extracted; no column mapping needed
      // guess mapping from headers
      const cols = r.data.columns.map((c) => c.name.toLowerCase());
      const find = (kw) => cols.findIndex((c) => kw.some((k) => c.includes(k)));
      const dateI = find(["date", "posted"]);
      const descI = find(["desc", "memo", "detail", "narrative", "transaction"]);
      const amtI = find(["amount", "value"]);
      const debI = find(["debit", "withdraw"]);
      const creI = find(["credit", "deposit"]);
      setMapping({
        date_col: dateI >= 0 ? dateI : 0,
        desc_col: descI >= 0 ? descI : 1,
        amount_mode: (debI >= 0 && creI >= 0 && amtI < 0) ? "split" : "single",
        amount_col: amtI >= 0 ? amtI : 2,
        debit_col: debI >= 0 ? debI : null,
        credit_col: creI >= 0 ? creI : null,
        has_header: true,
        expenses_positive: false,
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const confirmImport = async () => {
    setImporting(true);
    try {
      let body = {};
      if (parseResult.mode !== "pdf") {
        body = { ...mapping };
        body.date_col = Number(body.date_col);
        body.desc_col = Number(body.desc_col);
        if (body.amount_mode === "single") { body.amount_col = Number(body.amount_col); body.debit_col = null; body.credit_col = null; }
        else { body.debit_col = Number(body.debit_col); body.credit_col = Number(body.credit_col); body.amount_col = null; }
      }
      const r = await api.post(`/accounting/uploads/${parseResult.upload_id}/import`, body);
      toast.success(`Imported ${r.data.imported} transactions`);
      setParseResult(null);
      setAccountLabel("");
      loadData();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setImporting(false); }
  };

  const setTxn = async (id, patch) => {
    setTxnData((d) => ({ ...d, transactions: d.transactions.map((t) => (t.id === id ? { ...t, ...patch } : t)) }));
    try {
      await api.patch(`/accounting/transactions/${id}`, patch);
      loadData();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      loadData();
    }
  };

  const deleteUpload = async (u) => {
    if (!window.confirm(`Remove "${u.account_label}" and its ${u.row_count || ""} transactions?`)) return;
    try {
      await api.delete(`/accounting/uploads/${u.id}`);
      toast.success("Statement removed");
      loadData();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const doFinalize = async () => {
    if (!signName.trim()) { toast.error("Enter your name to sign"); return; }
    setFinalizing(true);
    try {
      await api.post("/accounting/pnl/finalize", { signed_name: signName, signed_title: signTitle }, { params: { property_id: propertyId, month } });
      toast.success("P&L finalized and saved to Document Center");
      setFinalizeOpen(false);
      loadData();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setFinalizing(false); }
  };

  const doReopen = async () => {
    if (!window.confirm("⚠ This statement is signed and locked.\n\nReopening will unlock the month for edits. The signed PDF already saved in the Document Center is kept as the record. Continue?")) return;
    try {
      await api.post("/accounting/pnl/reopen", {}, { params: { property_id: propertyId, month } });
      toast.success("Month reopened");
      loadData();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openCatEditor = () => { setCatDraft(categories.map((c) => ({ ...c }))); setCatOpen(true); };
  const [coaBusy, setCoaBusy] = useState(false);
  const loadMasterCoa = async (mode) => {
    if (mode === "replace" && !window.confirm("Replace this property's Chart of Accounts with the Master Hotel COA (209 accounts)? Any custom categories will be removed, and transactions categorized to removed accounts will need re-categorizing.")) return;
    setCoaBusy(true);
    try {
      const r = await api.post("/accounting/categories/load-master", null, { params: { property_id: propertyId, mode } });
      const rc = await api.get("/accounting/categories", { params: { property_id: propertyId } });
      setCategories(rc.data.categories || []);
      setCatDraft((rc.data.categories || []).map((c) => ({ ...c })));
      toast.success(`${mode === "merge" ? "Added missing accounts" : "Loaded Master Hotel COA"} — ${r.data.count} accounts`);
      loadData();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setCoaBusy(false); }
  };
  const saveCategories = async () => {
    try {
      await api.put("/accounting/categories", { categories: catDraft }, { params: { property_id: propertyId } });
      toast.success("Categories saved");
      setCatOpen(false);
      loadCategories();
      loadData();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // ---- auto-categorize rules ----
  const autoCategorize = async () => {
    setAutoBusy(true);
    try {
      const r = await api.post("/accounting/transactions/auto-categorize", null, { params: { property_id: propertyId, month } });
      toast.success(r.data.applied ? `Auto-categorized ${r.data.applied} transaction(s)` : "No matching rules for the remaining transactions");
      loadData();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setAutoBusy(false); }
  };
  const openRules = async () => {
    try {
      const r = await api.get("/accounting/rules", { params: { property_id: propertyId } });
      setRulesDraft((r.data.rules || []).map((x) => ({ ...x })));
      setRulesOpen(true);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const saveRules = async () => {
    try {
      await api.put("/accounting/rules", { rules: rulesDraft }, { params: { property_id: propertyId } });
      toast.success("Rules saved");
      setRulesOpen(false);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const openMakeRule = (t) => {
    setRuleTxn(t);
    setQuickKw(t.description || "");
    setQuickCat(t.category || "");
    setQuickKind((t.amount || 0) >= 0 ? "deposit" : "expense");
    setQuickAction("categorize");
  };
  const saveQuickRule = async () => {
    const action = quickAction || "categorize";
    if (!quickKw.trim()) { toast.error("Enter a keyword"); return; }
    if (action === "categorize" && !quickCat) { toast.error("Choose an account to code to"); return; }
    try {
      const r = await api.post("/accounting/rules/quick", { keyword: quickKw.trim(), category: action === "categorize" ? quickCat : "", kind: quickKind, action, month }, { params: { property_id: propertyId } });
      const verb = action === "exclude" ? "excluded" : action === "keep" ? "kept" : "coded";
      toast.success(`Rule saved — ${verb} ${r.data.applied} matching transaction(s)`);
      setRuleTxn(null); loadData();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const seedDeposits = async () => {
    try {
      const r = await api.post("/accounting/rules/seed-deposits", null, { params: { property_id: propertyId } });
      setRulesDraft((r.data.rules || []).map((x) => ({ ...x })));
      toast.success(r.data.added ? `Added ${r.data.added} deposit rule(s)` : "Deposit rules already present");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const seedFromVendors = async () => {
    try {
      const r = await api.post("/accounting/rules/seed-from-vendors", null, { params: { property_id: propertyId } });
      setRulesDraft((r.data.rules || []).map((x) => ({ ...x })));
      toast.success(r.data.added ? `Added ${r.data.added} vendor rule(s)` : "No new vendor rules to add (vendors need a default expense account)");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // ---- bulk select ----
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelected(new Set());
  const applyBulk = async () => {
    if (!bulkCat || selected.size === 0) return;
    try {
      await api.post("/accounting/transactions/bulk", { ids: [...selected], category: bulkCat });
      toast.success(`Applied to ${selected.size} transaction(s)`);
      clearSel(); setBulkCat(""); loadData();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const excludeBulk = async (val) => {
    if (selected.size === 0) return;
    try {
      await api.post("/accounting/transactions/bulk", { ids: [...selected], excluded: val });
      toast.success(`${val ? "Excluded" : "Included"} ${selected.size} transaction(s)`);
      clearSel(); loadData();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openSplit = (t) => {
    setSplitTxn(t);
    setSplitLines(t.splits?.length ? t.splits.map((s) => ({ ...s })) : [{ category: "", amount: "" }, { category: "", amount: "" }]);
  };
  const splitSum = splitLines.reduce((a, l) => a + (parseFloat(l.amount) || 0), 0);
  const splitTarget = splitTxn ? Math.abs(splitTxn.amount) : 0;
  const saveSplit = async () => {
    const lines = splitLines.filter((l) => l.category && parseFloat(l.amount) > 0).map((l) => ({ category: l.category, amount: parseFloat(l.amount) }));
    if (lines.length < 1) { toast.error("Add at least one split line"); return; }
    if (Math.abs(splitSum - splitTarget) > 0.005) { toast.error(`Splits must total ${money(splitTarget)}`); return; }
    try {
      await api.post(`/accounting/transactions/${splitTxn.id}/split`, { splits: lines });
      toast.success("Transaction split saved");
      setSplitTxn(null);
      loadData();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const removeSplit = async () => {
    try {
      await api.post(`/accounting/transactions/${splitTxn.id}/split`, { splits: [] });
      toast.success("Split removed");
      setSplitTxn(null);
      loadData();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // balance sheet
  const bsTotals = (lines) => {
    const asset = ["Current Assets", "Fixed Assets"], liab = ["Current Liabilities", "Long-Term Liabilities"];
    const sum = (secs) => lines.filter((l) => secs.includes(l.section)).reduce((a, l) => a + (parseFloat(l.amount) || 0), 0);
    const assets = sum(asset), liabs = sum(liab), equity = sum(["Equity"]);
    return { assets, liabilities: liabs, equity, liab_plus_equity: liabs + equity, balanced: Math.abs(assets - (liabs + equity)) < 0.01 };
  };
  const setBsLine = (i, patch) => setBs((b) => ({ ...b, lines: b.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  const addBsLine = (section) => setBs((b) => ({ ...b, lines: [...b.lines, { section, name: "", amount: 0 }] }));
  const removeBsLine = (i) => setBs((b) => ({ ...b, lines: b.lines.filter((_, j) => j !== i) }));
  const pullNetIncome = () => setBs((b) => ({ ...b, lines: b.lines.map((l) => (l.name === "Current Period Net Income" ? { ...l, amount: b.net_income_pnl } : l)) }));
  const saveBalanceSheet = async () => {
    setBsSaving(true);
    try {
      const r = await api.put("/accounting/balance-sheet", { lines: bs.lines }, { params: { property_id: propertyId, month } });
      setBs((b) => ({ ...b, lines: r.data.lines, totals: r.data.totals }));
      toast.success("Balance sheet saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBsSaving(false); }
  };

  const baseTxns = onlyUncat
    ? txnData.transactions.filter((t) => !t.before_start && !t.category && !t.splits?.length && !t.excluded)
    : txnData.transactions;
  const sortVal = (t) => {
    if (sort.key === "description") return (t.description || "").toLowerCase();
    if (sort.key === "category") return t.splits?.length ? "~~split" : (catLabel(t.category) || "").toLowerCase();
    if (sort.key === "date") return t.date || "";
    if (sort.key === "amount") return t.amount ?? 0;
    return 0;
  };
  const sortedTxns = sort.key
    ? [...baseTxns].sort((a, b) => {
        const va = sortVal(a); const vb = sortVal(b);
        if (va < vb) return sort.dir === "asc" ? -1 : 1;
        if (va > vb) return sort.dir === "asc" ? 1 : -1;
        return 0;
      })
    : baseTxns;
  const txns = [...sortedTxns].sort((a, b) => (a.before_start || a.excluded ? 1 : 0) - (b.before_start || b.excluded ? 1 : 0));
  const summary = txnData.summary || {};
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6" data-testid="accounting-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Accounting &amp; P&amp;L</h1>
          <p className="text-sm text-muted-foreground">Upload bank statements, categorize every transaction, and finalize a signed monthly P&amp;L.</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-0 flex-1 sm:flex-none">
            <Label className="text-xs">Property</Label>
            <Select value={propertyId || ""} onValueChange={setPropertyId}>
              <SelectTrigger className="w-full sm:w-52" data-testid="acct-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
              <SelectContent>
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-0 flex-1 sm:flex-none">
            <Label className="text-xs">Month</Label>
            <div className="flex items-center gap-1">
              <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="acct-month-prev" aria-label="Previous month"><ChevronLeft className="h-4 w-4" /></Button>
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="min-w-0 flex-1 text-center sm:w-40 sm:flex-none" data-testid="acct-month" />
              <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="acct-month-next" aria-label="Next month"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
      </div>

      {isFinal && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-700 dark:text-emerald-300" data-testid="acct-final-banner">
          <Lock className="h-4 w-4" /> Finalized by <b>{pnl.signed_name}</b>{pnl.signed_title ? ` (${pnl.signed_title})` : ""} on {String(pnl.finalized_at).slice(0, 10)}.
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto border-b">
        {[["transactions", "Transactions", ListChecks], ["pnl", "P&L Statement", PieChart], ["balance", "Balance Sheet", Scale]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`acct-tab-${k}`}
            className={`flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
        <button onClick={openCatEditor} data-testid="acct-edit-categories"
          className="ml-auto flex shrink-0 items-center gap-2 whitespace-nowrap px-3 py-2 text-sm text-muted-foreground hover:text-foreground">
          <Settings2 className="h-4 w-4" /> Categories
        </button>
      </div>

      {tab === "transactions" && (
        <div className="space-y-4">
          <Card className="p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[180px]">
                <Label className="text-xs">Account / statement name</Label>
                <Input value={accountLabel} onChange={(e) => setAccountLabel(e.target.value)} placeholder="e.g. Main Checking ...1234" data-testid="acct-account-label" disabled={isFinal} />
              </div>
              <label className={`inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 ${isFinal ? "pointer-events-none opacity-50" : ""}`} data-testid="acct-upload-btn">
                <Upload className="h-4 w-4" /> Upload statement (PDF / Excel / CSV)
                <input type="file" accept=".xlsx,.xlsm,.csv,.pdf" className="hidden" onChange={onPickFile} disabled={isFinal} />
              </label>
            </div>
            {txnData.uploads?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {txnData.uploads.map((u) => (
                  <Badge key={u.id} variant="secondary" className="flex items-center gap-2 py-1.5" data-testid={`acct-upload-${u.id}`}>
                    <FileSpreadsheet className="h-3.5 w-3.5" /> {u.account_label} · {u.row_count} rows
                    {!isFinal && <button onClick={() => deleteUpload(u)} className="ml-1 text-red-500 hover:text-red-600" data-testid={`acct-upload-del-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                  </Badge>
                ))}
              </div>
            )}
          </Card>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Transactions" value={summary.count || 0} />
            <Stat label="Money In" value={money(summary.money_in)} tone="green" />
            <Stat label="Money Out" value={money(summary.money_out)} tone="red" />
            <Stat label="Uncategorized" value={summary.uncategorized || 0} tone={summary.uncategorized ? "amber" : "green"} />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="text-sm text-muted-foreground">{txns.length} shown</div>
              {!isFinal && (
                <>
                  <Button variant="outline" size="sm" onClick={autoCategorize} disabled={autoBusy} data-testid="acct-auto-categorize">
                    {autoBusy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1 h-3.5 w-3.5" />} Auto-categorize
                  </Button>
                  <Button variant="ghost" size="sm" onClick={openRules} data-testid="acct-rules-btn">
                    <Settings2 className="mr-1 h-3.5 w-3.5" /> Rules
                  </Button>
                </>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={onlyUncat} onChange={(e) => setOnlyUncat(e.target.checked)} data-testid="acct-only-uncat" className="h-4 w-4" />
              Show uncategorized only
            </label>
          </div>

          {!isFinal && selected.size > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2" data-testid="acct-bulk-bar">
              <span className="text-sm font-semibold">{selected.size} selected</span>
              <div className="w-64"><AccountCombobox categories={categories} value={bulkCat} onChange={setBulkCat} testid="acct-bulk-cat" placeholder="— apply account to all —" /></div>
              <Button size="sm" onClick={applyBulk} disabled={!bulkCat} data-testid="acct-bulk-apply">Apply</Button>
              <Button size="sm" variant="outline" onClick={() => excludeBulk(true)} data-testid="acct-bulk-exclude">Exclude</Button>
              <Button size="sm" variant="ghost" onClick={() => excludeBulk(false)} data-testid="acct-bulk-include">Include</Button>
              <button onClick={clearSel} className="ml-auto text-muted-foreground hover:text-foreground" data-testid="acct-bulk-clear"><X className="h-4 w-4" /></button>
            </div>
          )}

          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/95 text-left text-xs uppercase text-muted-foreground backdrop-blur">
                  <tr>
                    <th className="px-3 py-2">
                      {!isFinal && (
                        <input type="checkbox" data-testid="acct-select-all" className="h-4 w-4"
                          checked={txns.length > 0 && txns.every((t) => selected.has(t.id))}
                          onChange={(e) => setSelected(e.target.checked ? new Set(txns.map((t) => t.id)) : new Set())} />
                      )}
                    </th>
                    <th className="px-3 py-2">
                      <button onClick={() => toggleSort("date")} className="inline-flex items-center gap-1 uppercase hover:text-foreground" data-testid="acct-sort-date">Date <ArrowUpDown className={`h-3 w-3 ${sort.key === "date" ? "text-primary" : "opacity-40"}`} /></button>
                    </th>
                    <th className="px-3 py-2">
                      <button onClick={() => toggleSort("description")} className="inline-flex items-center gap-1 uppercase hover:text-foreground" data-testid="acct-sort-description">Description <ArrowUpDown className={`h-3 w-3 ${sort.key === "description" ? "text-primary" : "opacity-40"}`} /></button>
                    </th>
                    <th className="px-3 py-2 text-right">
                      <button onClick={() => toggleSort("amount")} className="inline-flex items-center gap-1 uppercase hover:text-foreground" data-testid="acct-sort-amount">Amount <ArrowUpDown className={`h-3 w-3 ${sort.key === "amount" ? "text-primary" : "opacity-40"}`} /></button>
                    </th>
                    <th className="px-3 py-2">
                      <button onClick={() => toggleSort("category")} className="inline-flex items-center gap-1 uppercase hover:text-foreground" data-testid="acct-sort-category">Category <ArrowUpDown className={`h-3 w-3 ${sort.key === "category" ? "text-primary" : "opacity-40"}`} /></button>
                    </th>
                    <th className="px-3 py-2 text-center">Split</th>
                    <th className="px-3 py-2 text-center">Exclude</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={7} className="px-3 py-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
                  {!loading && txns.length === 0 && (
                    <tr><td colSpan={7} className="px-3 py-10 text-center text-muted-foreground">No transactions yet. Upload a bank statement to get started.</td></tr>
                  )}
                  {!loading && txns.map((t) => {
                    const needsAttn = t.category && catById[t.category]?.needs_split && !t.splits?.length;
                    const hasSplit = t.splits?.length > 0;
                    if (t.before_start) return (
                      <tr key={t.id} className="border-t bg-muted/30 opacity-50" data-testid={`acct-txn-${t.id}`}>
                        <td className="px-3 py-2" />
                        <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{t.date || "—"}</td>
                        <td className="px-3 py-2 line-through decoration-muted-foreground/50">{t.description}<span className="ml-2 text-xs text-muted-foreground">{t.account_label}</span></td>
                        <td className="whitespace-nowrap px-3 py-2 text-right font-medium text-muted-foreground">{money(t.amount)}</td>
                        <td colSpan={3} className="px-3 py-2"><span className="rounded border border-dashed px-2 py-0.5 text-xs font-medium text-muted-foreground" data-testid={`acct-txn-before-start-${t.id}`}>Before business start — not counted</span></td>
                      </tr>
                    );
                    return (
                    <tr key={t.id} className={`border-t ${t.excluded ? "opacity-45" : ""} ${!t.category && !hasSplit && !t.excluded ? "bg-amber-500/5" : ""} ${needsAttn ? "bg-orange-500/10" : ""} ${selected.has(t.id) ? "bg-primary/5" : ""}`} data-testid={`acct-txn-${t.id}`}>
                      <td className="px-3 py-2">
                        {!isFinal && (
                          <input type="checkbox" className="h-4 w-4" checked={selected.has(t.id)} onChange={() => toggleSel(t.id)} data-testid={`acct-txn-sel-${t.id}`} />
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{t.date || "—"}</td>
                      <td className="px-3 py-2">{t.description}<span className="ml-2 text-xs text-muted-foreground">{t.account_label}</span></td>
                      <td className={`whitespace-nowrap px-3 py-2 text-right font-medium ${t.amount < 0 ? "text-red-500" : "text-emerald-600"}`}>{money(t.amount)}</td>
                      <td className="px-3 py-2">
                        {hasSplit ? (
                          <div className="flex items-center gap-2 text-xs">
                            <span className="rounded bg-primary/15 px-1.5 py-0.5 font-medium text-primary">Split</span>
                            <span className="text-muted-foreground">{t.splits.map((s) => `${catLabel(s.category)} ${money(s.amount)}`).join(" · ")}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                          <AccountCombobox categories={categories} value={t.category || ""} onChange={(v) => setTxn(t.id, { category: v })}
                            disabled={isFinal || t.excluded} testid={`acct-txn-cat-${t.id}`} className="max-w-[240px]" />
                          {!isFinal && (
                            <button onClick={() => openMakeRule(t)} title="Make a rule from this description and apply to all matching rows"
                              data-testid={`acct-txn-makerule-${t.id}`}
                              className="shrink-0 rounded border p-1.5 text-muted-foreground hover:border-primary hover:text-primary"><Wand2 className="h-3.5 w-3.5" /></button>
                          )}
                          </div>
                        )}
                        {needsAttn && <div className="mt-1 text-[0.65rem] font-medium text-orange-500">Needs split (e.g. interest vs principal)</div>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {!isFinal && !t.excluded && (
                          <button onClick={() => openSplit(t)} data-testid={`acct-txn-split-${t.id}`}
                            className="rounded border px-2 py-1 text-xs font-medium hover:bg-muted">{hasSplit ? "Edit split" : "Split"}</button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input type="checkbox" checked={!!t.excluded} onChange={(e) => setTxn(t.id, { excluded: e.target.checked })} disabled={isFinal}
                          data-testid={`acct-txn-excl-${t.id}`} className="h-4 w-4" />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {tab === "pnl" && pnl && (
        <div className="space-y-4">
          {pnl.uncategorized > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm text-amber-700 dark:text-amber-300">
              <AlertTriangle className="h-4 w-4" /> {pnl.uncategorized} transaction(s) still need a category. Categorize or exclude them before finalizing.
            </div>
          )}
          <Card className="p-6" data-testid="acct-pnl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="text-lg font-bold">{properties.find((p) => p.id === propertyId)?.name}</div>
                <div className="text-sm text-muted-foreground">Profit &amp; Loss — {month}</div>
              </div>
              <Badge variant={isFinal ? "default" : "secondary"}>{isFinal ? "Finalized" : "Draft"}</Badge>
            </div>

            <Section title="Revenue" />
            {pnl.revenue.lines.length === 0 && <Row label="No operating revenue recorded for this month" muted />}
            {pnl.revenue.lines.map((l, i) => <Row key={i} label={l.name} value={l.amount} indent onClick={l.cat_id ? () => setDrillCat({ id: l.cat_id, name: l.name }) : undefined} />)}
            <Row label="Total Revenue" value={pnl.revenue.total} bold divider />

            <div className="h-4" />
            <Section title="Expenses" />
            {pnl.expense_groups.length === 0 && <Row label="No categorized expenses yet" muted />}
            {pnl.expense_groups.map((g) => (
              <div key={g.group}>
                <Row label={g.group} groupHead />
                {g.lines.map((l, i) => <Row key={i} label={l.name} value={l.amount} indent onClick={l.cat_id ? () => setDrillCat({ id: l.cat_id, name: l.name }) : undefined} />)}
                <Row label={`Subtotal — ${g.group}`} value={g.subtotal} subtotal />
              </div>
            ))}
            <Row label="Total Expenses" value={pnl.expense_total} bold divider />

            <div className="mt-4 flex items-center justify-between rounded-lg bg-primary/10 px-4 py-3">
              <span className="text-base font-bold">Net Operating Income</span>
              <span className={`text-base font-bold ${pnl.net_operating_income < 0 ? "text-red-500" : "text-emerald-600"}`} data-testid="acct-noi">{money(pnl.net_operating_income)}</span>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">Tip: click any expense or bank-income line to see the transactions in it and move any that are miscategorized. Revenue is pulled from Daily/Monthly Reports; expenses come from your categorized bank transactions.</p>
          </Card>

          <div className="flex flex-wrap items-center gap-3">
            {pnl.needs_attention > 0 && (
              <span className="rounded-md border border-orange-500/40 bg-orange-500/10 px-3 py-1.5 text-xs text-orange-600 dark:text-orange-300">
                {pnl.needs_attention} transaction(s) flagged "needs split" (e.g. mortgage) aren't split yet — you can still finalize, but review them first.
              </span>
            )}
            {!isFinal ? (
              <Button onClick={() => setFinalizeOpen(true)} disabled={pnl.uncategorized > 0} data-testid="acct-finalize-btn">
                <CheckCircle2 className="mr-2 h-4 w-4" /> Finalize &amp; Sign
              </Button>
            ) : (
              <>
                {pnl.document_id && (
                  <a href={`${api.defaults.baseURL}/documents/${pnl.document_id}/file?download=1`} target="_blank" rel="noreferrer">
                    <Button variant="outline" data-testid="acct-download-pdf"><Download className="mr-2 h-4 w-4" /> Download signed P&amp;L</Button>
                  </a>
                )}
                {canReopen ? (
                  <Button variant="outline" onClick={doReopen} data-testid="acct-reopen-btn"><LockOpen className="mr-2 h-4 w-4" /> Reopen month</Button>
                ) : (
                  <span className="rounded-md border border-muted px-3 py-1.5 text-xs text-muted-foreground">Signed &amp; locked — only the accountant can reopen this statement.</span>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {tab === "balance" && bs && (
        <div className="space-y-4" data-testid="acct-balance">
          <Card className="p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-lg font-bold">{properties.find((p) => p.id === propertyId)?.name}</div>
                <div className="text-sm text-muted-foreground">Balance Sheet — as of end of {month}</div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={pullNetIncome} data-testid="acct-bs-pull-ni">Pull Net Income from P&amp;L ({money(bs.net_income_pnl)})</Button>
                <Button size="sm" onClick={saveBalanceSheet} disabled={bsSaving || isFinal} data-testid="acct-bs-save">{bsSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save</Button>
              </div>
            </div>
            {(() => { const t = bsTotals(bs.lines); return (
              <div className="grid gap-6 md:grid-cols-2">
                {[["Assets", ["Current Assets", "Fixed Assets"]], ["Liabilities & Equity", ["Current Liabilities", "Long-Term Liabilities", "Equity"]]].map(([side, sections]) => (
                  <div key={side}>
                    <div className="mb-2 border-b pb-1 text-sm font-bold uppercase tracking-wide text-primary">{side}</div>
                    {sections.map((section) => (
                      <div key={section} className="mb-3">
                        <div className="mb-1 flex items-center justify-between text-xs font-semibold text-muted-foreground">
                          <span>{section}</span>
                          <button onClick={() => addBsLine(section)} className="text-primary hover:underline" data-testid={`acct-bs-add-${section.replace(/\s/g, "-")}`}>+ add</button>
                        </div>
                        {bs.lines.map((l, i) => l.section === section ? (
                          <div key={i} className="mb-1 flex items-center gap-2">
                            <Input value={l.name} onChange={(e) => setBsLine(i, { name: e.target.value })} placeholder="Line item" className="h-8 flex-1 text-sm" disabled={isFinal} />
                            <Input type="number" step="0.01" value={l.amount} onChange={(e) => setBsLine(i, { amount: e.target.value })} className="h-8 w-32 text-right text-sm" disabled={isFinal} data-testid={`acct-bs-amt-${i}`} />
                            {!isFinal && <button onClick={() => removeBsLine(i)} className="text-red-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                          </div>
                        ) : null)}
                      </div>
                    ))}
                    <div className="mt-2 flex items-center justify-between border-t pt-2 text-sm font-bold">
                      <span>Total {side === "Assets" ? "Assets" : "Liabilities + Equity"}</span>
                      <span className="tabular-nums">{money(side === "Assets" ? t.assets : t.liab_plus_equity)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ); })()}
            {(() => { const t = bsTotals(bs.lines); return (
              <div className={`mt-4 rounded-lg px-4 py-2 text-sm font-semibold ${t.balanced ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300" : "bg-red-500/10 text-red-500"}`} data-testid="acct-bs-balanced">
                {t.balanced ? "✓ Balanced — Assets equal Liabilities + Equity" : `Out of balance by ${money(Math.abs(t.assets - t.liab_plus_equity))} — Assets ${money(t.assets)} vs Liab+Equity ${money(t.liab_plus_equity)}`}
              </div>
            ); })()}
            <p className="mt-3 text-xs text-muted-foreground">Enter your figures — totals calculate automatically. "Pull Net Income from P&amp;L" fills the current-period net income line from this month's P&amp;L.</p>
          </Card>
        </div>
      )}

      {/* Mapping / PDF-preview dialog */}
      <Dialog open={!!parseResult} onOpenChange={(o) => !o && setParseResult(null)}>
        <DialogContent className="max-w-3xl" data-testid="acct-mapping-dialog">
          <DialogHeader>
            <DialogTitle>{parseResult?.mode === "pdf" ? `Review extracted transactions — ${parseResult?.filename}` : `Map columns — ${parseResult?.filename}`}</DialogTitle>
            <DialogDescription>
              {parseResult?.mode === "pdf"
                ? `AI found ${parseResult?.total_rows} transaction(s). Review below, then import — you'll categorize each one next. (Balances are ignored.)`
                : "Tell us which columns hold the date, description and amount. Preview below."}
            </DialogDescription>
          </DialogHeader>
          {parseResult && parseResult.mode === "pdf" && (
            <div className="max-h-[55vh] overflow-auto rounded-md border" data-testid="acct-pdf-preview">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted"><tr><th className="px-2 py-1 text-left font-medium">Date</th><th className="px-2 py-1 text-left font-medium">Description</th><th className="px-2 py-1 text-right font-medium">Amount</th></tr></thead>
                <tbody>
                  {(parseResult.transactions || []).map((t, i) => (
                    <tr key={i} className="border-t">
                      <td className="whitespace-nowrap px-2 py-1">{t.date || "—"}</td>
                      <td className="px-2 py-1">{t.description}</td>
                      <td className={`whitespace-nowrap px-2 py-1 text-right font-medium ${t.amount < 0 ? "text-red-500" : "text-emerald-600"}`}>{money(t.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {parseResult && parseResult.mode !== "pdf" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <ColSel label="Date column" cols={parseResult.columns} value={mapping.date_col} onChange={(v) => setMapping((m) => ({ ...m, date_col: v }))} testid="map-date" />
                <ColSel label="Description column" cols={parseResult.columns} value={mapping.desc_col} onChange={(v) => setMapping((m) => ({ ...m, desc_col: v }))} testid="map-desc" />
                <div>
                  <Label className="text-xs">Amount format</Label>
                  <select value={mapping.amount_mode} onChange={(e) => setMapping((m) => ({ ...m, amount_mode: e.target.value }))} data-testid="map-mode"
                    className="mt-1 w-full rounded-md border bg-background px-2 py-2 text-sm">
                    <option value="single">Single amount column</option>
                    <option value="split">Separate debit &amp; credit</option>
                  </select>
                </div>
                {mapping.amount_mode === "single" ? (
                  <>
                    <ColSel label="Amount column" cols={parseResult.columns} value={mapping.amount_col} onChange={(v) => setMapping((m) => ({ ...m, amount_col: v }))} testid="map-amount" />
                    <label className="col-span-2 flex items-center gap-2 self-end text-sm">
                      <input type="checkbox" checked={mapping.expenses_positive} onChange={(e) => setMapping((m) => ({ ...m, expenses_positive: e.target.checked }))} className="h-4 w-4" data-testid="map-exp-positive" />
                      In my file, expenses/withdrawals are positive numbers
                    </label>
                  </>
                ) : (
                  <>
                    <ColSel label="Debit (out) column" cols={parseResult.columns} value={mapping.debit_col ?? 0} onChange={(v) => setMapping((m) => ({ ...m, debit_col: v }))} testid="map-debit" />
                    <ColSel label="Credit (in) column" cols={parseResult.columns} value={mapping.credit_col ?? 0} onChange={(v) => setMapping((m) => ({ ...m, credit_col: v }))} testid="map-credit" />
                  </>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={mapping.has_header} onChange={(e) => setMapping((m) => ({ ...m, has_header: e.target.checked }))} className="h-4 w-4" data-testid="map-header" />
                First row is a header (skip it)
              </label>
              <div className="max-h-52 overflow-auto rounded-md border">
                <table className="w-full text-xs">
                  <thead className="bg-muted"><tr>{parseResult.columns.map((c) => <th key={c.index} className="px-2 py-1 text-left font-medium">{c.name}</th>)}</tr></thead>
                  <tbody>
                    {parseResult.sample.map((row, i) => (
                      <tr key={i} className="border-t">{row.map((cell, j) => <td key={j} className="px-2 py-1">{cell}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setParseResult(null)}>Cancel</Button>
            <Button onClick={confirmImport} disabled={importing} data-testid="acct-import-confirm">
              {importing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Import transactions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finalize dialog */}
      <Dialog open={finalizeOpen} onOpenChange={setFinalizeOpen}>
        <DialogContent data-testid="acct-finalize-dialog">
          <DialogHeader>
            <DialogTitle>Finalize &amp; sign P&amp;L — {month}</DialogTitle>
            <DialogDescription>This locks the month and saves a signed PDF to the Document Center under "P&amp;L Statements".</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Your name (signature)</Label>
              <Input value={signName} onChange={(e) => setSignName(e.target.value)} placeholder="Full name" data-testid="acct-sign-name" />
            </div>
            <div>
              <Label className="text-xs">Title (optional)</Label>
              <Input value={signTitle} onChange={(e) => setSignTitle(e.target.value)} placeholder="e.g. Staff Accountant, CPA" data-testid="acct-sign-title" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinalizeOpen(false)}>Cancel</Button>
            <Button onClick={doFinalize} disabled={finalizing} data-testid="acct-finalize-confirm">
              {finalizing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Finalize &amp; Sign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category drill-down dialog */}
      <Dialog open={!!drillCat} onOpenChange={(o) => !o && setDrillCat(null)}>
        <DialogContent className="max-w-2xl" data-testid="acct-drill-dialog">
          <DialogHeader>
            <DialogTitle>{drillCat?.name}</DialogTitle>
            <DialogDescription>Transactions in this category. Change a category to move a line where it belongs.</DialogDescription>
          </DialogHeader>
          {drillCat && (() => {
            const rows = txnData.transactions
              .filter((t) => !t.excluded && (t.category === drillCat.id || (t.splits || []).some((s) => s.category === drillCat.id)))
              .map((t) => {
                const split = (t.splits || []).find((s) => s.category === drillCat.id);
                return { t, amount: split ? split.amount : Math.abs(t.amount), isSplit: !!split };
              });
            const ccRows = (pnl?.cc_receipts || []).filter((r) => r.category === drillCat.id);
            return (
              <div className="max-h-[55vh] overflow-auto rounded-md border">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-muted"><tr><th className="px-2 py-1 text-left">Date</th><th className="px-2 py-1 text-left">Description</th><th className="px-2 py-1 text-right">Amount</th><th className="px-2 py-1">Move to</th></tr></thead>
                  <tbody>
                    {rows.length === 0 && ccRows.length === 0 && <tr><td colSpan={4} className="px-2 py-6 text-center text-muted-foreground">No transactions in this category.</td></tr>}
                    {rows.map(({ t, amount, isSplit }) => (
                      <tr key={t.id} className="border-t" data-testid={`acct-drill-txn-${t.id}`}>
                        <td className="whitespace-nowrap px-2 py-1">{t.date}</td>
                        <td className="px-2 py-1">{t.description}{isSplit && <span className="ml-1 rounded bg-primary/15 px-1 text-[0.6rem] text-primary">split</span>}</td>
                        <td className="whitespace-nowrap px-2 py-1 text-right font-medium">{money(amount)}</td>
                        <td className="px-2 py-1">
                          {isSplit ? (
                            <button className="text-primary underline" onClick={() => { setDrillCat(null); openSplit(t); }}>edit split</button>
                          ) : (
                            <div className="max-w-[200px]"><AccountCombobox categories={categories} value={t.category || ""} onChange={(v) => setTxn(t.id, { category: v })} testid={`acct-drill-move-${t.id}`} /></div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {ccRows.map((r) => (
                      <tr key={`cc-${r.id}`} className="border-t bg-sky-500/5" data-testid={`acct-drill-cc-${r.id}`}>
                        <td className="whitespace-nowrap px-2 py-1">{r.date}</td>
                        <td className="px-2 py-1">{r.merchant}{r.memo && <span className="ml-1 text-muted-foreground">· {r.memo}</span>}
                          <span className="ml-1 rounded bg-sky-500/15 px-1 text-[0.6rem] text-sky-700 dark:text-sky-400">CC receipt{r.card_last4 ? ` ·${r.card_last4}` : ""}</span></td>
                        <td className="whitespace-nowrap px-2 py-1 text-right font-medium">{money(r.amount)}</td>
                        <td className="px-2 py-1 text-muted-foreground">
                          <a href="/invoices" className="text-primary underline" data-testid={`acct-drill-cc-edit-${r.id}`}>Edit in CC Receipts</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
          <DialogFooter><Button variant="outline" onClick={() => setDrillCat(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split dialog */}
      <Dialog open={!!splitTxn} onOpenChange={(o) => !o && setSplitTxn(null)}>
        <DialogContent className="max-w-lg" data-testid="acct-split-dialog">
          <DialogHeader>
            <DialogTitle>Split transaction</DialogTitle>
            <DialogDescription>
              {splitTxn?.description} · {money(splitTxn?.amount)} — allocate across categories (e.g. Mortgage Interest + Mortgage Principal). Amounts must total {money(splitTarget)}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {splitLines.map((l, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`acct-split-line-${i}`}>
                <div className="flex-1"><AccountCombobox categories={categories} value={l.category} onChange={(v) => setSplitLines((s) => s.map((x, j) => j === i ? { ...x, category: v } : x))} testid={`acct-split-cat-${i}`} placeholder="— category —" /></div>
                <Input type="number" step="0.01" value={l.amount} onChange={(e) => setSplitLines((s) => s.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))}
                  className="w-32 text-right text-sm" placeholder="0.00" data-testid={`acct-split-amt-${i}`} />
                <button onClick={() => setSplitLines((s) => s.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setSplitLines((s) => [...s, { category: "", amount: "" }])} data-testid="acct-split-add"><Plus className="mr-1 h-4 w-4" /> Add line</Button>
            <div className={`flex items-center justify-between rounded-md px-3 py-2 text-sm font-semibold ${Math.abs(splitSum - splitTarget) < 0.005 ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"}`} data-testid="acct-split-total">
              <span>Allocated {money(splitSum)} of {money(splitTarget)}</span>
              <span>{Math.abs(splitSum - splitTarget) < 0.005 ? "✓ balances" : money(splitTarget - splitSum) + " left"}</span>
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            {splitTxn?.splits?.length ? <Button variant="ghost" className="text-red-500" onClick={removeSplit} data-testid="acct-split-remove">Remove split</Button> : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSplitTxn(null)}>Cancel</Button>
              <Button onClick={saveSplit} disabled={Math.abs(splitSum - splitTarget) >= 0.005} data-testid="acct-split-save"><Scissors className="mr-2 h-4 w-4" /> Save split</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Categories editor */}
      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent className="max-w-3xl" data-testid="acct-cat-dialog">
          <DialogHeader>
            <DialogTitle>Chart of Accounts</DialogTitle>
            <DialogDescription>Account #, department (group) and name used to build this property's P&amp;L. Load your Master Hotel COA, then categorize transactions against these accounts.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border bg-muted/30 p-2.5">
            <span className="text-xs font-semibold text-muted-foreground">Master Hotel COA ({MASTER_COA_COUNT} accounts):</span>
            <Button variant="outline" size="sm" onClick={() => loadMasterCoa("replace")} disabled={coaBusy} data-testid="acct-coa-load-replace">
              {coaBusy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />} Load (replace all)
            </Button>
            <Button variant="outline" size="sm" onClick={() => loadMasterCoa("merge")} disabled={coaBusy} data-testid="acct-coa-load-merge">
              <Plus className="mr-1 h-3.5 w-3.5" /> Add missing accounts
            </Button>
          </div>
          <div className="mt-1 flex items-center gap-2 px-1 text-[0.65rem] font-semibold uppercase tracking-wide text-muted-foreground">
            <span className="w-20">Acct #</span><span className="w-40">Department</span><span className="flex-1">Account name</span><span className="w-24">Type</span>
          </div>
          <div className="max-h-[50vh] space-y-2 overflow-auto pr-1">
            {catDraft.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={c.code || ""} onChange={(e) => setCatDraft((d) => d.map((x, j) => j === i ? { ...x, code: e.target.value } : x))} placeholder="#" className="w-20 font-mono" data-testid={`acct-cat-code-${i}`} />
                <Input value={c.group} onChange={(e) => setCatDraft((d) => d.map((x, j) => j === i ? { ...x, group: e.target.value } : x))} placeholder="Department" className="w-40" />
                <Input value={c.name} onChange={(e) => setCatDraft((d) => d.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder="Account name" className="flex-1" />
                <select value={c.type} onChange={(e) => setCatDraft((d) => d.map((x, j) => j === i ? { ...x, type: e.target.value } : x))} className="w-24 rounded-md border bg-background px-2 py-2 text-sm">
                  <option value="expense">Expense</option>
                  <option value="income">Income</option>
                  <option value="non_pnl">Not on P&L</option>
                </select>
                <label className="flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground" title="Nudge the accountant to split this (e.g. mortgage = interest + principal)">
                  <input type="checkbox" checked={!!c.needs_split} onChange={(e) => setCatDraft((d) => d.map((x, j) => j === i ? { ...x, needs_split: e.target.checked } : x))} className="h-4 w-4" />
                  split
                </label>
                <button onClick={() => setCatDraft((d) => d.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => setCatDraft((d) => [...d, { code: "", group: "Other", name: "", type: "expense" }])} data-testid="acct-cat-add"><Plus className="mr-1 h-4 w-4" /> Add account</Button>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatOpen(false)}>Cancel</Button>
            <Button onClick={saveCategories} data-testid="acct-cat-save">Save chart of accounts</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-categorize rules */}
      <Dialog open={rulesOpen} onOpenChange={setRulesOpen}>
        <DialogContent className="max-w-2xl" data-testid="acct-rules-dialog">
          <DialogHeader>
            <DialogTitle>Auto-categorize rules</DialogTitle>
            <DialogDescription>When a transaction's description contains the keyword, it's handled automatically on upload and when you click "Auto-categorize". <b>Categorize</b> codes it to an account, <b>Exclude</b> drops it from the P&L (e.g. merchant/card deposits already captured in Manager Flash), and <b>Keep</b> protects a deposit from being excluded (e.g. real cash bank deposits). Rules only fill blanks — they never overwrite a category you set yourself.</DialogDescription>
          </DialogHeader>
          <div className="mt-1 flex items-center gap-2 px-1 text-[0.65rem] font-semibold uppercase tracking-wide text-muted-foreground">
            <span className="flex-1">If description contains…</span><span className="w-24">Applies to</span><span className="w-28">Action</span><span className="flex-1">Then…</span>
          </div>
          <div className="max-h-[50vh] space-y-2 overflow-auto pr-1">
            {rulesDraft.length === 0 && <p className="px-1 py-6 text-center text-sm text-muted-foreground">No rules yet. Add one below (e.g. "merchant deposit" → Exclude).</p>}
            {rulesDraft.map((r, i) => {
              const action = r.action || "categorize";
              return (
              <div key={i} className="flex items-center gap-2">
                <Input value={r.keyword} onChange={(e) => setRulesDraft((d) => d.map((x, j) => j === i ? { ...x, keyword: e.target.value } : x))} placeholder="merchant deposit" className="flex-1" data-testid={`acct-rule-kw-${i}`} />
                <select value={r.kind || "any"} onChange={(e) => setRulesDraft((d) => d.map((x, j) => j === i ? { ...x, kind: e.target.value } : x))} className="w-24 rounded-md border bg-background px-2 py-2 text-sm" data-testid={`acct-rule-kind-${i}`}>
                  <option value="any">Any</option>
                  <option value="deposit">Deposits</option>
                  <option value="expense">Expenses</option>
                </select>
                <select value={action} onChange={(e) => setRulesDraft((d) => d.map((x, j) => j === i ? { ...x, action: e.target.value } : x))} className="w-28 rounded-md border bg-background px-2 py-2 text-sm" data-testid={`acct-rule-action-${i}`}>
                  <option value="categorize">Categorize</option>
                  <option value="exclude">Exclude</option>
                  <option value="keep">Keep</option>
                </select>
                {action === "categorize" ? (
                  <div className="flex-1"><AccountCombobox categories={categories} value={r.category || ""} onChange={(v) => setRulesDraft((d) => d.map((x, j) => j === i ? { ...x, category: v } : x))} testid={`acct-rule-cat-${i}`} /></div>
                ) : (
                  <div className="flex-1 px-2 text-xs text-muted-foreground" data-testid={`acct-rule-action-note-${i}`}>{action === "exclude" ? "Excluded from P&L" : "Kept — never auto-excluded"}</div>
                )}
                <button onClick={() => setRulesDraft((d) => d.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            );})}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setRulesDraft((d) => [...d, { keyword: "", kind: "deposit", category: "", action: "categorize" }])} data-testid="acct-rule-add"><Plus className="mr-1 h-4 w-4" /> Add rule</Button>
            <Button variant="outline" size="sm" onClick={seedDeposits} data-testid="acct-rule-seed-deposits"><Download className="mr-1 h-4 w-4" /> Add deposit rules</Button>
            <Button variant="outline" size="sm" onClick={seedFromVendors} data-testid="acct-rule-seed-vendors"><Download className="mr-1 h-4 w-4" /> Add rules from vendors</Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRulesOpen(false)}>Cancel</Button>
            <Button onClick={saveRules} data-testid="acct-rules-save">Save rules</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Make rule from a transaction */}
      <Dialog open={!!ruleTxn} onOpenChange={(v) => !v && setRuleTxn(null)}>
        <DialogContent className="max-w-lg" data-testid="acct-makerule-dialog">
          <DialogHeader>
            <DialogTitle>Make a rule from this transaction</DialogTitle>
            <DialogDescription>Save a keyword→account rule and apply it to every matching transaction this month. Matches anywhere in the description (so trailing dates / batch numbers are fine).</DialogDescription>
          </DialogHeader>
          {ruleTxn && (
            <div className="space-y-3">
              <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
                <div className="text-muted-foreground">Original description</div>
                <div className="font-medium">{ruleTxn.description}</div>
                <div className={`mt-1 text-xs ${ruleTxn.amount < 0 ? "text-red-500" : "text-emerald-600"}`}>{money(ruleTxn.amount)}</div>
              </div>
              <div>
                <Label className="text-xs font-semibold">Match when description contains</Label>
                <Input value={quickKw} onChange={(e) => setQuickKw(e.target.value)} placeholder='e.g. "DEPOSIT"' data-testid="acct-makerule-kw" />
                <p className="mt-1 text-xs text-muted-foreground">Tip: shorten to the part that repeats (drop batch numbers / dates).</p>
              </div>
              <div className="flex gap-2">
                <div className="w-32">
                  <Label className="text-xs font-semibold">Applies to</Label>
                  <select value={quickKind} onChange={(e) => setQuickKind(e.target.value)} className="w-full rounded-md border bg-background px-2 py-2 text-sm" data-testid="acct-makerule-kind">
                    <option value="any">Any</option>
                    <option value="deposit">Deposits</option>
                    <option value="expense">Expenses</option>
                  </select>
                </div>
                <div className="w-32">
                  <Label className="text-xs font-semibold">Action</Label>
                  <select value={quickAction} onChange={(e) => setQuickAction(e.target.value)} className="w-full rounded-md border bg-background px-2 py-2 text-sm" data-testid="acct-makerule-action">
                    <option value="categorize">Categorize</option>
                    <option value="exclude">Exclude</option>
                    <option value="keep">Keep</option>
                  </select>
                </div>
                {quickAction === "categorize" ? (
                  <div className="flex-1">
                    <Label className="text-xs font-semibold">Code to account</Label>
                    <AccountCombobox categories={categories} value={quickCat} onChange={setQuickCat} testid="acct-makerule-cat" />
                  </div>
                ) : (
                  <div className="flex-1">
                    <Label className="text-xs font-semibold">Result</Label>
                    <div className="flex h-10 items-center rounded-md border bg-muted/30 px-3 text-xs text-muted-foreground" data-testid="acct-makerule-action-note">{quickAction === "exclude" ? "Excluded from the P&L" : "Kept — never auto-excluded"}</div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRuleTxn(null)}>Cancel</Button>
            <Button onClick={saveQuickRule} data-testid="acct-makerule-save">Save &amp; apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const c = tone === "green" ? "text-emerald-600" : tone === "red" ? "text-red-500" : tone === "amber" ? "text-amber-500" : "";
  return (
    <Card className="p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold ${c}`}>{value}</div>
    </Card>
  );
}

function ColSel({ label, cols, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <select value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testid}
        className="mt-1 w-full rounded-md border bg-background px-2 py-2 text-sm">
        {cols.map((c) => <option key={c.index} value={c.index}>{c.name}</option>)}
      </select>
    </div>
  );
}

function Section({ title }) {
  return <div className="mb-1 border-b pb-1 text-sm font-bold uppercase tracking-wide text-primary">{title}</div>;
}

function Row({ label, value, indent, bold, subtotal, groupHead, muted, divider, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag onClick={onClick} data-testid={onClick ? "acct-pnl-line" : undefined}
      className={`flex w-full items-center justify-between py-1 text-left text-sm ${divider ? "mt-1 border-t pt-2" : ""} ${bold ? "font-bold" : ""} ${groupHead ? "mt-2 font-semibold" : ""} ${subtotal ? "italic text-muted-foreground" : ""} ${muted ? "text-muted-foreground" : ""} ${onClick ? "cursor-pointer rounded hover:bg-primary/5" : ""}`}>
      <span className={`${indent ? "pl-4" : subtotal ? "pl-4" : ""} ${onClick ? "underline decoration-dotted underline-offset-2" : ""}`}>{label}</span>
      {value !== undefined && <span className="tabular-nums">{money(value)}</span>}
    </Tag>
  );
}
